<template>
  <div class="min-h-screen flex items-center justify-center relative" style="background-color: var(--bg-primary);">
    <div class="absolute top-4 right-4 z-10">
      <LanguageSwitcher variant="compact" :show-label="false" />
    </div>
    <div class="max-w-md w-full space-y-8 p-8">
      <!-- Logo e título -->
      <div class="text-center">
        <div class="mx-auto h-96 w-96 flex items-center justify-center">
          <img 
            :src="logoImage" 
            :alt="t('auth.login.logoAlt')" 
            class="h-96 w-96 object-contain"
          />
        </div>
        <div class="mt-6 flex justify-center">
          <!-- <img 
            :src="logoTextImage" 
            alt="Backlog" 
            class="h-12 object-contain"
          /> -->
        </div>
        <p class="mt-2 text-sm" style="color: var(--text-secondary);">
          {{ t('auth.login.title') }}
        </p>
      </div>

      <!-- Formulário de login -->
      <form @submit.prevent="handleLogin" class="mt-8 space-y-6">
        <div class="space-y-4">
          <div>
            <label for="email" class="block text-sm font-medium mb-2" style="color: var(--text-primary);">
              {{ t('auth.login.email') }}
            </label>
            <input
              id="email"
              v-model="form.email"
              type="email"
              required
              class="input-field"
              :placeholder="t('auth.login.emailPlaceholder')"
              :disabled="authStore.isLoading"
            />
          </div>
          
          <div>
            <label for="password" class="block text-sm font-medium mb-2" style="color: var(--text-primary);">
              {{ t('auth.login.password') }}
            </label>
            <input
              id="password"
              v-model="form.password"
              type="password"
              required
              class="input-field"
              :placeholder="t('auth.login.passwordPlaceholder')"
              :disabled="authStore.isLoading"
            />
          </div>
        </div>

        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <input
              id="remember-me"
              v-model="form.remember"
              type="checkbox"
              class="h-4 w-4 text-discord-blurple focus:ring-discord-blurple border-discord-dark-200 rounded"
            />
            <label for="remember-me" class="ml-2 block text-sm" style="color: var(--text-secondary);">
              {{ t('auth.login.rememberMe') }}
            </label>
          </div>

          <div class="text-sm">
            <a href="#" class="font-medium text-discord-blurple hover:text-blue-400">
              {{ t('auth.login.forgotPassword') }}
            </a>
          </div>
        </div>

        <div>
          <button
            type="submit"
            :disabled="authStore.isLoading"
            class="w-full btn-primary py-3 text-base font-medium"
          >
            <span v-if="authStore.isLoading" class="flex items-center justify-center">
              <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ t('auth.login.submitting') }}
            </span>
            <span v-else>{{ t('auth.login.submit') }}</span>
          </button>
        </div>

        <div class="text-center">
          <p class="text-sm" style="color: var(--text-secondary);">
            {{ t('auth.login.noAccount') }}
            <router-link to="/register" class="font-medium" style="color: var(--info);" @click="trackMeta('InitiateCheckout')">
              {{ t('auth.login.registerLink') }}
            </router-link>
          </p>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import { reactive, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useThemeStore } from '../stores/theme'
import { useAccountAccessBlock } from '../composables/useAccountAccessBlock'
import LanguageSwitcher from '../components/LanguageSwitcher.vue'
import logoLight from '../assets/logo_b.png'
import logoDark from '../assets/logo_w.png'
import logoTextLight from '../assets/logo_l_p.png'
import logoTextDark from '../assets/logo_l_w.png'
import { trackMeta } from '../utils/metaPixel'

const router = useRouter()
const authStore = useAuthStore()
const { openModal: openAccountBlockModal } = useAccountAccessBlock()
const themeStore = useThemeStore()
const { t } = useI18n()

const form = reactive({
  email: '',
  password: '',
  remember: false
})

const isLightTheme = computed(() => themeStore.isLight)
const logoImage = computed(() => {
  return isLightTheme.value ? logoLight : logoDark
})
const logoTextImage = computed(() => {
  return isLightTheme.value ? logoTextLight : logoTextDark
})

const handleLogin = async () => {
  console.log('=== [LOGIN] HandleLogin chamado ===')
  console.log('[LOGIN] Form data:', { email: form.email })
  
  const result = await authStore.login({
    email: form.email,
    senha: form.password
  })
  
  console.log('[LOGIN] Resultado do login:', result)
  
  if (result.success) {
    console.log('[LOGIN] ✅ Login bem-sucedido!')
    if (authStore.isAccessBlocked) {
      openAccountBlockModal()
    }
    console.log('[LOGIN] 🔄 Os workspaces serão carregados DO BANCO após o redirecionamento...')
    
    await new Promise(resolve => setTimeout(resolve, 100))
    
    const inviteToken = localStorage.getItem('inviteToken')
    if (inviteToken) {
      console.log('[LOGIN] 📧 Token de convite encontrado, redirecionando...')
      localStorage.removeItem('inviteToken')
      router.push(`/convite/${inviteToken}`)
    } else {
      console.log('[LOGIN] 🏠 Redirecionando para dashboard...')
      console.log('[LOGIN] 📡 Workspaces serão buscados do BANCO no router.afterEach')
      router.push('/dashboard')
    }
  } else {
    console.log('[LOGIN] ❌ Login falhou:', result.error)
  }
  
  console.log('=== [LOGIN] Fim do handleLogin ===')
}
</script>
